import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Dropdown, Form, Input, Label, Message, Pagination, Popup, Table,} from 'semantic-ui-react';
import {Link} from 'react-router-dom';
import {
  API,
  copy,
  loadChannelModels,
  setPromptShown,
  shouldShowPrompt,
  showError,
  showInfo,
  showSuccess,
  timestamp2string,
} from '../helpers';

import {ITEMS_PER_PAGE} from '../constants';
import {
  buildChannelOptions,
  findDescriptor,
  loadChannelDescriptors,
} from '../helpers/channelDescriptor';
import {renderGroup, renderNumber} from '../helpers/render';

function renderTimestamp(timestamp) {
  return <>{timestamp2string(timestamp)}</>;
}

// buildType2Label 由渠道清单（内置常量 + 后端下发的 ChannelDescriptor）构建
// type -> 选项 映射，供列表标签渲染使用（PRD §5.12 层1 / 决策 D7）。
function buildType2Label(options, t) {
  const map = {};
  for (let i = 0; i < options.length; i++) {
    map[options[i].value] = options[i];
  }
  map[0] = {
    value: 0,
    text: t('channel.table.status_unknown'),
    color: 'grey',
  };
  return map;
}

function renderType(type, t, type2label) {
  const option = type2label[type];
  // 未知 type 兜底（渠道插件化 AC7）：库里可能残留当前构建不认识的渠道行
  // （如已摘除的扩展渠道 / 历史遗留号段）。此时不得渲染成裸露数字或 undefined，
  // 统一渲染为「未知渠道」，保证列表不白屏、不报错。
  if (!option) {
    return (
      <Label basic color='grey'>
        {t('channel.table.unknown_channel', { type })}
      </Label>
    );
  }
  return (
    <Label basic color={option.color}>
      {option.text}
    </Label>
  );
}

// renderBalance 渲染渠道余额格。descriptor 为后端下发的渠道清单项：当且仅当
// capabilities.supports_balance === true 时按「积分」直接显示数值（无货币符号），
// 其余情况回退既有内置 switch。前端不含任何私有渠道类型/名称硬编码。
function renderBalance(type, balance, t, descriptor) {
  if (descriptor?.capabilities?.supports_balance === true) {
    return <span>{renderNumber(balance)}</span>;
  }
  switch (type) {
    case 1: // OpenAI
        if (balance === 0) {
            return <span>{t('channel.table.balance_not_supported')}</span>;
        }
      return <span>${balance.toFixed(2)}</span>;
    case 4: // CloseAI
      return <span>¥{balance.toFixed(2)}</span>;
    case 8: // 自定义
      return <span>${balance.toFixed(2)}</span>;
    case 5: // OpenAI-SB
      return <span>¥{(balance / 10000).toFixed(2)}</span>;
    case 10: // AI Proxy
      return <span>{renderNumber(balance)}</span>;
    case 12: // API2GPT
      return <span>¥{balance.toFixed(2)}</span>;
    case 13: // AIGC2D
      return <span>{renderNumber(balance)}</span>;
    case 20: // OpenRouter
      return <span>${balance.toFixed(2)}</span>;
    case 36: // DeepSeek
      return <span>¥{balance.toFixed(2)}</span>;
    case 44: // SiliconFlow
      return <span>¥{balance.toFixed(2)}</span>;
    default:
      return <span>{t('channel.table.balance_not_supported')}</span>;
  }
}

// buildChannelCopyEnvelope 把完整渠道响应收敛为剪贴板 v1 信封（仅白名单字段）。
// 只导出可编辑字段，绝不整包转储 API 对象；Go 侧可空指针（base_url/other/model_mapping/
// system_prompt/priority）在此归一为表单默认值，group/models 保持逗号字符串形态。
// 必填字段缺失或类型不符时抛错，由调用方统一走失败提示。
export function buildChannelCopyEnvelope(channel) {
  if (!channel || typeof channel !== 'object' || Array.isArray(channel)) {
    throw new Error('invalid channel payload');
  }
  if (typeof channel.type !== 'number' || !Number.isFinite(channel.type)) {
    throw new Error('invalid channel type');
  }
  for (const field of ['name', 'group', 'models', 'key']) {
    if (typeof channel[field] !== 'string') {
      throw new Error(`invalid channel ${field}`);
    }
  }
  if (typeof channel.config !== 'string') {
    throw new Error('invalid channel config');
  }
  let priority = channel.priority;
  if (priority === null || priority === undefined) {
    priority = 1;
  } else if (typeof priority !== 'number' || !Number.isFinite(priority)) {
    throw new Error('invalid channel priority');
  }
  const normalizeNullable = (value) =>
    value === null || value === undefined ? '' : String(value);
  return {
    myapi_channel: 1,
    channel: {
      type: channel.type,
      name: channel.name,
      group: channel.group,
      models: channel.models,
      key: channel.key,
      base_url: normalizeNullable(channel.base_url),
      other: normalizeNullable(channel.other),
      model_mapping: normalizeNullable(channel.model_mapping),
      system_prompt: normalizeNullable(channel.system_prompt),
      priority,
      config: channel.config === '' ? '{}' : channel.config,
    },
  };
}

function isShowDetail() {
  return localStorage.getItem('show_detail') === 'true';
}

const promptID = 'detail';

const ChannelsTable = () => {
  const { t, i18n } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  // 搜索结果模式下禁用分页增量加载，避免全量数据混入搜索结果
  const [searchMode, setSearchMode] = useState(false);
  const [updatingBalance, setUpdatingBalance] = useState(false);
  const [showPrompt, setShowPrompt] = useState(shouldShowPrompt(promptID));
  const [showDetail, setShowDetail] = useState(isShowDetail());
  // 后端下发的渠道清单（PRD §5.12 层1）：默认构建仅含内置渠道，扩展渠道由后端下发清单。
  const [descriptors, setDescriptors] = useState([]);
  const channelOptions = buildChannelOptions(descriptors, i18n.language);
  const type2label = buildType2Label(channelOptions, t);

  const processChannelData = (channel) => {
    if (channel.models === '') {
      channel.models = [];
      channel.test_model = '';
    } else {
      channel.models = channel.models.split(',');
      if (channel.models.length > 0) {
        channel.test_model = channel.models[0];
      }
      channel.model_options = channel.models.map((model) => {
        return {
          key: model,
          text: model,
          value: model,
        };
      });
      console.log('channel', channel);
    }
    return channel;
  };

  const loadChannels = async (startIdx) => {
    try {
      const res = await API.get(`/api/channel/?p=${startIdx}`);
      const { success, message, data } = res.data;
      if (success) {
        let localChannels = data.map(processChannelData);
        if (startIdx === 0) {
          setChannels(localChannels);
        } else {
          let newChannels = [...channels];
          newChannels.splice(
            startIdx * ITEMS_PER_PAGE,
            data.length,
            ...localChannels
          );
          setChannels(newChannels);
        }
      } else {
        showError(message);
      }
    } finally {
      // 复位必须放在 finally：初始加载/翻页/刷新任一环节失败都要解除表格遮罩
      setLoading(false);
    }
  };

  const onPaginationChange = (e, { activePage }) => {
    // 错误提示已由 api 拦截器统一弹出，此处仅需阻断 unhandled rejection
    (async () => {
      if (
        !searchMode &&
        activePage === Math.ceil(channels.length / ITEMS_PER_PAGE) + 1
      ) {
        // In this case we have to load more data and then append them.
        await loadChannels(activePage - 1);
      }
      setActivePage(activePage);
    })().catch(() => {});
  };

  const refresh = async () => {
    setLoading(true);
    setSearchMode(false);
    try {
      await loadChannels(activePage - 1);
    } finally {
      setLoading(false);
    }
  };

  const toggleShowDetail = () => {
    setShowDetail(!showDetail);
    localStorage.setItem('show_detail', (!showDetail).toString());
  };

  useEffect(() => {
    // 错误提示已由 api 拦截器统一弹出，此处仅需阻断 unhandled rejection
    loadChannels(0)
      .then()
      .catch(() => {});
    loadChannelModels().then().catch(() => {});
    loadChannelDescriptors()
      .then((list) => setDescriptors(list))
      .catch(() => {});
  }, []);

  const manageChannel = async (id, action, idx, value) => {
    let data = { id };
    let res;
    switch (action) {
      case 'delete':
        res = await API.delete(`/api/channel/${id}/`);
        break;
      case 'enable':
        data.status = 1;
        res = await API.put('/api/channel/', data);
        break;
      case 'disable':
        data.status = 2;
        res = await API.put('/api/channel/', data);
        break;
      case 'priority':
        if (value === '') {
          return;
        }
        data.priority = parseInt(value);
        res = await API.put('/api/channel/', data);
        break;
      case 'weight':
        if (value === '') {
          return;
        }
        data.weight = parseInt(value);
        if (data.weight < 0) {
          data.weight = 0;
        }
        res = await API.put('/api/channel/', data);
        break;
      case 'reset':
        res = await API.get(`/api/channel/reset/${id}`);
        break;
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('channel.messages.operation_success'));
      let newChannels = [...channels];
      let realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
      if (action === 'delete') {
        newChannels[realIdx].deleted = true;
        setChannels(newChannels);
      } else {
        let channel = res.data.data;
        newChannels[realIdx].status = channel.status;
        setChannels(newChannels);
      }
    } else {
      showError(message);
    }
  };

  const copyChannel = async (id) => {
    try {
      const res = await API.get(`/api/channel/copy/${id}`);
      const { success, message, data } = res.data;
      if (!success) {
        showError(message || t('channel.messages.copy_failed'));
        return;
      }
      const envelope = buildChannelCopyEnvelope(data);
      const okay = await copy(JSON.stringify(envelope));
      if (okay) {
        showSuccess(t('channel.messages.copy_success'));
      } else {
        showError(t('channel.messages.copy_failed'));
      }
    } catch (e) {
      // 请求被拒 / 信封非法：统一提示失败，绝不回显密钥或完整响应体
      showError(t('channel.messages.copy_failed'));
    }
  };

  const renderStatus = (status, t) => {
    switch (status) {
      case 1:
        return (
          <Label basic color='green'>
            {t('channel.table.status_enabled')}
          </Label>
        );
      case 2:
        return (
          <Popup
            trigger={
              <Label basic color='red'>
                {t('channel.table.status_disabled')}
              </Label>
            }
            content={t('channel.table.status_disabled_tip')}
            basic
          />
        );
      case 3:
        return (
          <Popup
            trigger={
              <Label basic color='yellow'>
                {t('channel.table.status_auto_disabled')}
              </Label>
            }
            content={t('channel.table.status_auto_disabled_tip')}
            basic
          />
        );
      default:
        return (
          <Label basic color='grey'>
            {t('channel.table.status_unknown')}
          </Label>
        );
    }
  };

  const renderResponseTime = (responseTime, t) => {
    let time = responseTime / 1000;
    time = time.toFixed(2) + 's';
    if (responseTime === 0) {
      return (
        <Label basic color='grey'>
          {t('channel.table.not_tested')}
        </Label>
      );
    } else if (responseTime <= 1000) {
      return (
        <Label basic color='green'>
          {time}
        </Label>
      );
    } else if (responseTime <= 3000) {
      return (
        <Label basic color='olive'>
          {time}
        </Label>
      );
    } else if (responseTime <= 5000) {
      return (
        <Label basic color='yellow'>
          {time}
        </Label>
      );
    } else {
      return (
        <Label basic color='red'>
          {time}
        </Label>
      );
    }
  };

  const searchChannels = async () => {
    // 防重复提交：上一次搜索未返回前忽略再次提交，避免较慢的旧响应覆盖新结果
    if (searching) return;
    try {
      if (searchKeyword === '') {
        // if keyword is blank, load files instead.
        setSearchMode(false);
        await loadChannels(0);
        setActivePage(1);
        return;
      }
      setSearching(true);
      setSearchMode(true);
      const res = await API.get(
        `/api/channel/search?keyword=${encodeURIComponent(searchKeyword)}`
      );
      const { success, message, data } = res.data;
      if (success) {
        let localChannels = data.map(processChannelData);
        setChannels(localChannels);
        setActivePage(1);
      } else {
        showError(message);
      }
    } finally {
      setSearching(false);
    }
  };

  const switchTestModel = async (idx, model) => {
    let newChannels = [...channels];
    let realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
    newChannels[realIdx].test_model = model;
    setChannels(newChannels);
  };

  const testChannel = async (id, name, idx, m) => {
    const res = await API.get(`/api/channel/test/${id}?model=${m}`);
    const { success, message, time, model } = res.data;
    if (success) {
      let newChannels = [...channels];
      let realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
      newChannels[realIdx].response_time = time * 1000;
      newChannels[realIdx].test_time = Date.now() / 1000;
      setChannels(newChannels);
      showSuccess(
        t('channel.messages.test_success', { name, model, time, message })
      );
    } else {
      showError(message);
    }
  };

  const testChannels = async (scope) => {
    const res = await API.get(`/api/channel/test?scope=${scope}`);
    const { success, message } = res.data;
    if (success) {
      showInfo(t('channel.messages.test_all_started'));
    } else {
      showError(message);
    }
  };

  const deleteAllDisabledChannels = async () => {
    const res = await API.delete(`/api/channel/disabled`);
    const { success, message, data } = res.data;
    if (success) {
      showSuccess(
        t('channel.messages.delete_disabled_success', { count: data })
      );
      await refresh();
    } else {
      showError(message);
    }
  };

  const updateChannelBalance = async (id, name, idx) => {
    const res = await API.get(`/api/channel/update_balance/${id}/`);
    const { success, message, balance } = res.data;
    if (success) {
      let newChannels = [...channels];
      let realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
      newChannels[realIdx].balance = balance;
      newChannels[realIdx].balance_updated_time = Date.now() / 1000;
      setChannels(newChannels);
      showSuccess(t('channel.messages.balance_update_success', { name }));
    } else {
      showError(message);
    }
  };

  const updateAllChannelsBalance = async () => {
    setUpdatingBalance(true);
    const res = await API.get(`/api/channel/update_balance`);
    const { success, message } = res.data;
    if (success) {
      showInfo(t('channel.messages.all_balance_updated'));
    } else {
      showError(message);
    }
    setUpdatingBalance(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortChannel = (key) => {
    if (channels.length === 0) return;
    setLoading(true);
    let sortedChannels = [...channels];
    sortedChannels.sort((a, b) => {
      if (!isNaN(a[key])) {
        // If the value is numeric, subtract to sort
        return a[key] - b[key];
      } else {
        // If the value is not numeric, sort as strings
        return ('' + a[key]).localeCompare(b[key]);
      }
    });
    if (sortedChannels[0].id === channels[0].id) {
      sortedChannels.reverse();
    }
    setChannels(sortedChannels);
    setLoading(false);
  };

  return (
    <>
      <Form onSubmit={searchChannels}>
        <Form.Input
          icon='search'
          fluid
          iconPosition='left'
          placeholder={t('channel.search')}
          value={searchKeyword}
          loading={searching}
          onChange={handleKeywordChange}
        />
      </Form>
      {showPrompt && (
        <Message
          onDismiss={() => {
            setShowPrompt(false);
            setPromptShown(promptID);
          }}
        >
          {t('channel.balance_notice')}
          <br />
          {t('channel.test_notice')}
          <br />
          {t('channel.detail_notice')}
        </Message>
      )}
      <div className='table-scroll-wrapper'>
      <Table unstackable basic={'very'} compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('id');
              }}
            >
              {t('channel.table.id')}
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('name');
              }}
            >
              {t('channel.table.name')}
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('group');
              }}
            >
              {t('channel.table.group')}
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('type');
              }}
            >
              {t('channel.table.type')}
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('status');
              }}
            >
              {t('channel.table.status')}
            </Table.HeaderCell>
            <Table.HeaderCell
              className='hide-on-mobile'
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('response_time');
              }}
            >
              {t('channel.table.response_time')}
            </Table.HeaderCell>
            <Table.HeaderCell
              className='hide-on-mobile'
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('balance');
              }}
            >
              {t('channel.table.balance')}
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortChannel('priority');
              }}
              hidden={!showDetail}
            >
              {t('channel.table.priority')}
            </Table.HeaderCell>
            <Table.HeaderCell hidden={!showDetail}>
              {t('channel.table.test_model')}
            </Table.HeaderCell>
            <Table.HeaderCell>{t('channel.table.actions')}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {channels
            .slice(
              (activePage - 1) * ITEMS_PER_PAGE,
              activePage * ITEMS_PER_PAGE
            )
            .map((channel, idx) => {
              if (channel.deleted) return <></>;
              return (
                <Table.Row key={channel.id}>
                  <Table.Cell>{channel.id}</Table.Cell>
                  <Table.Cell>
                    {channel.name ? channel.name : t('channel.table.no_name')}
                  </Table.Cell>
                  <Table.Cell>{renderGroup(channel.group)}</Table.Cell>
                  <Table.Cell>{renderType(channel.type, t, type2label)}</Table.Cell>
                  <Table.Cell>{renderStatus(channel.status, t)}</Table.Cell>
                  <Table.Cell className='hide-on-mobile'>
                    <Popup
                      content={
                        channel.test_time
                          ? renderTimestamp(channel.test_time)
                          : t('channel.table.not_tested')
                      }
                      key={channel.id}
                      trigger={renderResponseTime(channel.response_time, t)}
                      basic
                    />
                  </Table.Cell>
                  <Table.Cell className='hide-on-mobile'>
                    <Popup
                      trigger={
                        <span
                          onClick={() => {
                            updateChannelBalance(channel.id, channel.name, idx);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {renderBalance(
                            channel.type,
                            channel.balance,
                            t,
                            findDescriptor(descriptors, channel.type)
                          )}
                        </span>
                      }
                      content={t('channel.table.click_to_update')}
                      basic
                    />
                  </Table.Cell>
                  <Table.Cell hidden={!showDetail}>
                    <Popup
                      trigger={
                        <Input
                          type='number'
                          defaultValue={channel.priority}
                          onBlur={(event) => {
                            manageChannel(
                              channel.id,
                              'priority',
                              idx,
                              event.target.value
                            );
                          }}
                        >
                          <input style={{ maxWidth: '60px' }} />
                        </Input>
                      }
                      content={t('channel.table.priority_tip')}
                      basic
                    />
                  </Table.Cell>
                  <Table.Cell hidden={!showDetail}>
                    <Dropdown
                      placeholder={t('channel.table.select_test_model')}
                      selection
                      options={channel.model_options}
                      defaultValue={channel.test_model}
                      onChange={(event, data) => {
                        switchTestModel(idx, data.value);
                      }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <div
                      className='scroll-x-nowrap'
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '2px',
                        rowGap: '6px',
                      }}
                    >
                      <Button
                        size={'tiny'}
                        positive
                        onClick={() => {
                          testChannel(
                            channel.id,
                            channel.name,
                            idx,
                            channel.test_model
                          );
                        }}
                      >
                        {t('channel.buttons.test')}
                      </Button>
                      <Popup
                        trigger={
                          <Button size='tiny' negative>
                            {t('channel.buttons.delete')}
                          </Button>
                        }
                        on='click'
                        flowing
                        hoverable
                      >
                        <Button
                          size={'tiny'}
                          negative
                          onClick={() => {
                            manageChannel(channel.id, 'delete', idx);
                          }}
                        >
                          {t('channel.buttons.confirm_delete')} {channel.name}
                        </Button>
                      </Popup>
                      <Button
                        size={'tiny'}
                        onClick={() => {
                          manageChannel(
                            channel.id,
                            channel.status === 1 ? 'disable' : 'enable',
                            idx
                          );
                        }}
                      >
                        {channel.status === 1
                          ? t('channel.buttons.disable')
                          : t('channel.buttons.enable')}
                      </Button>
                      <Button
                        size={'tiny'}
                        onClick={() => {
                          manageChannel(channel.id, 'reset', idx);
                        }}
                      >
                        {t('channel.buttons.reset')}
                      </Button>
                      <Button
                        size={'tiny'}
                        onClick={() => {
                          copyChannel(channel.id);
                        }}
                      >
                        {t('channel.buttons.copy')}
                      </Button>
                      <Button
                        size={'tiny'}
                        as={Link}
                        to={'/channel/edit/' + channel.id}
                      >
                        {t('channel.buttons.edit')}
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
        </Table.Body>

      </Table>
      </div>
      <div className='table-footer-toolbar scroll-x-nowrap'>
        <Button size='tiny' as={Link} to='/channel/add' loading={loading}>
          {t('channel.buttons.add')}
        </Button>
        <Button
          size='tiny'
          loading={loading}
          onClick={() => {
            testChannels('all');
          }}
        >
          {t('channel.buttons.test_all')}
        </Button>
        <Button
          size='tiny'
          loading={loading}
          onClick={() => {
            testChannels('disabled');
          }}
        >
          {t('channel.buttons.test_disabled')}
        </Button>
        <Popup
          trigger={
            <Button size='tiny' loading={loading}>
              {t('channel.buttons.delete_disabled')}
            </Button>
          }
          on='click'
          flowing
          hoverable
        >
          <Button
            size='tiny'
            loading={loading}
            negative
            onClick={deleteAllDisabledChannels}
          >
            {t('channel.buttons.confirm_delete_disabled')}
          </Button>
        </Popup>
        <Button size='tiny' onClick={refresh} loading={loading}>
          {t('channel.buttons.refresh')}
        </Button>
        <Button size='tiny' onClick={toggleShowDetail}>
          {showDetail
            ? t('channel.buttons.hide_detail')
            : t('channel.buttons.show_detail')}
        </Button>
        <Pagination
          className='table-footer-pagination'
          floated='right'
          activePage={activePage}
          onPageChange={onPaginationChange}
          size='tiny'
          siblingRange={1}
          totalPages={
            Math.ceil(channels.length / ITEMS_PER_PAGE) +
            (channels.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
          }
        />
      </div>
    </>
  );
};

export default ChannelsTable;
